import { Injectable, isDevMode } from '@angular/core';
import { UserStorageService } from '../user-storage-service/user-storage.service';
import { GatewayDaoService } from 'src/app/dao/gateways-dao-services/gateway-dao.service';
import { Subject } from 'rxjs/internal/Subject';
import { tap, map } from 'rxjs/operators';
import { Gateway } from 'src/app/models/gateways-models/gateway.model';
import { GatewayProvider } from 'src/app/models/gateways-models/gateway-provider.model';
import { GatewayType } from 'src/app/models/gateways-models/gateway-type.model';
import { UserService } from '../user-service/user.service';
import { of } from 'rxjs/internal/observable/of';
import { EMPTY } from 'rxjs';
import { GatewayTransaction } from 'src/app/models/gateways-models/gateways-transaction.model';
import { CenterCurrency } from 'src/app/models/center-model/center-currency.model';

@Injectable({
  providedIn: 'root'
})
export class PaymentsService {

  private refreshTransactionsListener = new Subject<any>();
  private refreshGatewaysListener = new Subject<any>();
  private refreshGatewayListener = new Subject<any>();
  private filterGatewaysListener = new Subject<any>();
  private searchGatewaysListener = new Subject<any>();
  private toggleGatewaysDispositionListener = new Subject<any>();
  private accessToken = 'GTW';

  constructor(private userStorageService: UserStorageService,
              private userService: UserService,
              private gatewayDaoService: GatewayDaoService) { }

  public getGatewaysCounts() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.gatewayDaoService.getGatewaysCounts(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          if (o) {
            return {
              availablePaymentConfigs: o['data']['payments']['nbAvailables'],
              unavailablePaymentConfigs: o['data']['payments']['nbDisabled']
            };
          }
        })
      );
    } else {
      return of({
        availablePaymentConfigs: '-',
        unavailablePaymentConfigs: '-'
      });
    }
  }

  public getTransactions(from, to) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.gatewayDaoService.getTransactions(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), from, to).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const transactions = new Array<GatewayTransaction>();
          o['data']['traces'].forEach(t => {
            const transaction = new GatewayTransaction();
            Object.assign(transaction, t);
            const currency = new CenterCurrency();
            Object.assign(currency, t['currency']);
            transaction['currency'] = currency;
            transactions.push(transaction);
          });
          return transactions;
        })
      );
    }
    return EMPTY;
  }

  public getTransactionsKPI(from, to) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.gatewayDaoService.getTransactionsKPI(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), from, to).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data']['kpi'])
      );
    }
    return EMPTY;
  }

  public getGateways() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.gatewayDaoService.getGateways(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const gateways = new Array<Gateway>();
          o['data']['paymentConfigs'].forEach(g => {
            const gateway = new Gateway();
            Object.assign(gateway, g);
            const provider = new GatewayProvider();
            Object.assign(provider, g['gateway']);
            gateway.Gateway = provider;
            const type = new GatewayType();
            Object.assign(type, g['type']);
            gateway.Type = type;
            gateways.push(gateway);
          });
          return gateways;
        })
      );
    }
    return EMPTY;
  }

  public getGatewayInfo(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.gatewayDaoService.getGatewayInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const gateway = new Gateway();
          Object.assign(gateway, o['data']['id']);
          const provider = new GatewayProvider();
          Object.assign(provider, o['data']['id']['gateway']);
          gateway.Gateway = provider;
          const type = new GatewayType();
          Object.assign(type, o['data']['id']['type']);
          gateway.Type = type;
          return gateway;
        })
      );
    }
    return EMPTY;
  }

  public getProviders() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.gatewayDaoService.getProviders(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const gateways = new Array<GatewayProvider>();
          o['data']['gateways'].forEach(g => {
            const gateway = new GatewayProvider();
            Object.assign(gateway, g);
            gateways.push(gateway);
          });
          return gateways;
        })
      );
    }
    return EMPTY;
  }

  public getTypes() {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.gatewayDaoService.getTypes(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID()).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => {
          const gateways = new Array<GatewayType>();
          o['data']['types'].forEach(g => {
            const gateway = new GatewayType();
            Object.assign(gateway, g);
            gateways.push(gateway);
          });
          return gateways;
        })
      );
    }
    return EMPTY;
  }

  public addNewGateway(form) {
    if (this.userService.accessGranted(this.accessToken)) {
      const image = form.get('image').value;
      const active = form.get('active').value;
      const name = form.get('name').value;
      const provider = form.get('provider').value.InternalId;
      const type = form.get('type').value.InternalId;
      const invoiceprefix = form.get('invoiceprefix').value ? form.get('invoiceprefix').value : '';
      const accessOnsite = form.get('accessOnsite').value;
      const accessOnline = form.get('accessOnline').value;
      const properties = form.get('properties').value ? JSON.stringify(this.arrayToObject(form.get('properties').value)) : '';
      return this.gatewayDaoService.addNewGateway(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), image, active, name, provider, type, invoiceprefix, accessOnsite, accessOnline, properties).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateGatewayImage(internalId, imageUrl) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.gatewayDaoService.updateGatewayImage(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, imageUrl).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public updateGatewayStatus(active, internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.gatewayDaoService.updateGatewayStatus(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), active, internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data'])
      );
    }
    return EMPTY;
  }

  public updateGatewayInfo(internalId, form) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.gatewayDaoService.updateGatewayInfo(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, form.get('active').value, form.get('name').value, form.get('provider').value ? form.get('provider').value.InternalId : null, form.get('type').value ? form.get('type').value.InternalId : null, form.get('invoicePrefix').value, form.get('accessOnsite').value, form.get('accessOnline').value).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data'])
      );
    }
    return EMPTY;
  }

  public updateGatewayProperties(internalId, properties) {
    if (this.userService.accessGranted(this.accessToken)) {
      const stringifiedProperties = JSON.stringify(this.arrayToObject(properties));
      return this.gatewayDaoService.updateGatewayProperties(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId, stringifiedProperties).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o['data'])
      );
    }
    return EMPTY;
  }

  public deleteGateway(internalId) {
    if (this.userService.accessGranted(this.accessToken)) {
      return this.gatewayDaoService.deleteGateway(this.userStorageService.getContext(), this.userStorageService.getLang(), this.userStorageService.getUDID(), internalId).pipe(
        tap(o => !isDevMode() || console.dir(o)),
        map(o => o)
      );
    }
    return EMPTY;
  }

  public listenRefreshTransactions() {
    return this.refreshTransactionsListener.asObservable();
  }

  public refreshTransactions(from, to) {
    this.refreshTransactionsListener.next({ from, to });
  }

  public refreshGateways() {
    this.refreshGatewaysListener.next();
  }
  public listenRefreshGateways() {
    return this.refreshGatewaysListener.asObservable();
  }

  public refreshGateway() {
    this.refreshGatewayListener.next();
  }

  public listenRefreshGateway() {
    return this.refreshGatewayListener.asObservable();
  }

  public filterGateways(opts) {
    this.filterGatewaysListener.next(opts);
  }

  public listenFilterGateways() {
    return this.filterGatewaysListener.asObservable();
  }

  public searchGateways(filterKey) {
    this.searchGatewaysListener.next(filterKey);
  }

  public listenSearchGateways() {
    return this.searchGatewaysListener.asObservable();
  }

  public toggleGatewaysDisposition(dis) {
    this.toggleGatewaysDispositionListener.next(dis);
  }

  public listenToggleGatewaysDisposition() {
    return this.toggleGatewaysDispositionListener.asObservable();
  }

  private arrayToObject(array) {
    const o = new Object();
    array.forEach(i => o[i.key] = i.value);
    return o;
  }
}
